<template>
    <div>
        <!-- Header -->
        <section class="bg-grey-lighten-4 border-b">
            <div class="m-container py-8">
                <div
                    class="d-flex align-center justify-space-between flex-wrap ga-3"
                >
                    <div>
                        <h1 class="titel-h1 font-weight-bold">
                            Meine Merkliste
                        </h1>
                        <p class="text-body-2 text-medium-emphasis mt-1">
                            {{ wishlistStore.totalItems }}
                            {{
                                wishlistStore.totalItems === 1
                                    ? "Artikel"
                                    : "Artikel"
                            }}
                            gespeichert
                        </p>
                    </div>
                    <MBtn
                        v-if="wishlistStore.totalItems > 0"
                        variant="text"
                        color="secondary"
                        size="small"
                        class="text-none"
                        @click="showClearDialog = true"
                    >
                        <MIcon start :size="16" icon="delete-outline" />
                        Alle entfernen
                    </MBtn>
                </div>
            </div>
        </section>

        <div class="m-container py-8">
            <!-- Login Banner for guests -->
            <MAlert
                v-if="!authStore.isAuthenticated"
                type="info"
                variant="tonal"
                class="mb-6"
                rounded
            >
                <div
                    class="d-flex align-center justify-space-between flex-wrap ga-2"
                >
                    <div>
                        <strong>Merkliste sichern?</strong>
                        Melde dich an, um deine Merkliste auf allen Geraeten zu
                        speichern.
                    </div>
                    <MBtn
                        color="primary"
                        variant="flat"
                        size="small"
                        to="/auth/login"
                        class="text-none"
                    >
                        Anmelden
                    </MBtn>
                </div>
            </MAlert>

            <!-- Skeleton Loading -->
            <div class="m-row" v-if="wishlistStore.loading">
                <div class="m-col m-col-6 m-col-sm-4 m-col-md-3" v-for="n in 4" :key="n">
                    <ProductCardSkeleton />
                </div>
            </div>

            <!-- Wishlist Items -->
            <template v-else-if="wishlistStore.totalItems > 0">
                <div class="m-row">
                    <div
                        v-for="item in wishlistProducts"
                        :key="item.id"
                        class="m-col m-col-6 m-col-sm-4 m-col-md-3"
                    >
                        <ProductCard :product="item" />
                    </div>
                </div>
            </template>

            <!-- Empty State -->
            <div v-else class="text-center py-16">
                <MIcon :size="80" color="grey-lighten-2" icon="heart-outline" class="mb-4" />
                <h2 class="titel-h2 text-medium-emphasis mb-2">
                    Deine Merkliste ist leer
                </h2>
                <p
                    class="text-body-2 text-medium-emphasis mb-6"
                    style="max-width: 400px; margin: 0 auto"
                >
                    Speichere Möbel, die dir gefallen, und vergleiche sie später
                    in Ruhe.
                </p>
                <MBtn
                    color="primary"
                    variant="flat"
                    rounded="pill"
                    to="/kategorien"
                    class="text-none"
                >
                    Kategorien entdecken
                    <MIcon end icon="arrow-right" />
                </MBtn>
            </div>
        </div>

        <!-- Clear Confirmation Dialog -->
        <MDialog v-model="showClearDialog" max-width="400">
            <MCard rounded="md">
                <MCardTitle class="text-subtitle-1 font-weight-bold pt-5">
                    Merkliste leeren?
                </MCardTitle>
                <MCardText class="text-body-2 text-medium-emphasis">
                    Möchtest du wirklich alle
                    {{ wishlistStore.totalItems }} Artikel aus deiner Merkliste
                    entfernen? Diese Aktion kann nicht rückgängig gemacht
                    werden.
                </MCardText>
                <MCardActions class="pa-4 pt-0">
                    <span class="m-spacer" />
                    <MBtn
                        variant="text"
                        class="text-none"
                        @click="showClearDialog = false"
                    >
                        Abbrechen
                    </MBtn>
                    <MBtn
                        color="secondary"
                        variant="flat"
                        class="text-none"
                        @click="clearAll"
                    >
                        Alle entfernen
                    </MBtn>
                </MCardActions>
            </MCard>
        </MDialog>
    </div>
</template>

<script setup lang="ts">
import { useWishlistStore } from "~/stores/wishlist";
import { useAuthStore } from "~/stores/auth";

const wishlistStore = useWishlistStore();
const authStore = useAuthStore();
const showClearDialog = ref(false);

const wishlistProducts = computed(() => {
    return [...wishlistStore.items]
        .sort(
            (a, b) =>
                new Date(b.addedAt).getTime() - new Date(a.addedAt).getTime(),
        )
        .map((item) => ({
            id: item.productId,
            name: item.name,
            price: item.price,
            image: item.image,
            affiliateUrl: item.affiliateUrl || "#",
            merchantName: item.merchantName,
            ean: item.ean,
        }));
});

const clearAll = () => {
    wishlistStore.clearWishlist();
    showClearDialog.value = false;
};

useSeoMeta({
    title: "Merkliste",
    description:
        "Deine gespeicherten Möbel auf einen Blick. Vergleiche Preise und finde das beste Angebot.",
    robots: "noindex, nofollow",
});
</script>

<style scoped>
.border-b {
    border-bottom: 1px solid rgba(0, 0, 0, 0.08);
}
</style>
